<template>
  <div class="right">
    <div class="panel pv-panel-green-benefit">
      <div class="panel-title pv-panel-title-row">
        🌿 绿色效益
        <a href="#" class="pv-export-link pv-export-link--spaced" @click.prevent="greenExport">
          📥 导出
        </a>
      </div>
      <div class="num-card">
        <div class="num-value pv-num-value-md">{{ co2Text }}</div>
        <div class="num-label">累计减排CO₂ (吨)</div>
      </div>
      <div class="num-row pv-mt-4">
        <div class="num-item green">
          <div class="val">{{ treesText }}</div>
          <div class="lbl">等效植树 (棵)</div>
        </div>
        <div class="num-item orange">
          <div class="val">{{ monthGenMWhText }}</div>
          <div class="lbl">本月发电 (MWh)</div>
        </div>
      </div>
      <div class="pv-green-metrics">
        <div>
          <span>已节约电费</span>
          <span class="pv-accent-green">{{ costSavedText }}</span>
        </div>
        <div>
          <span>光伏总装机</span>
          <span class="pv-accent-cyan">{{ installedMwText }}</span>
        </div>
      </div>
    </div>

    <div class="panel pv-gauge-stack">
      <div class="panel-title">🎯 能源自给率</div>
      <PvGaugeChart
        ref="gaugeChart"
        :photovoltaic-ratio="pvRatio"
        :grid-ratio="gridRatio"
      />
      <div class="pv-ratio-foot">
        光伏占比 <span style="color: #ffa500; font-weight: bold">{{ pvRatioText }}</span>
        ｜ 市电占比 <span style="color: #00d4ff; font-weight: bold">{{ gridRatioText }}</span>
      </div>
    </div>

    <div class="panel">
      <div class="panel-title">🌤️ 环境参数（济源均值）</div>
      <div class="env-grid">
        <div class="env-item">
          <div class="ev">{{ envTemp }}</div>
          <div class="el">室外温度</div>
        </div>
        <div class="env-item">
          <div class="ev">{{ envHumid }}</div>
          <div class="el">相对湿度</div>
        </div>
        <div class="env-item">
          <div class="ev">{{ envPm25 }}</div>
          <div class="el">PM2.5</div>
        </div>
        <div class="env-item">
          <div class="ev">{{ envLight }}</div>
          <div class="el">光辐射均值</div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-title pv-panel-title-row">
        📊 站点能耗排名
        <a href="#" class="pv-export-link" @click.prevent="rankExport">📥 导出</a>
      </div>
      <ul v-if="rankRows.length" class="rank-list">
        <li v-for="(row, idx) in rankRows" :key="'rk-' + row.spaceId + '-' + idx">
          <span class="rank-num" :class="rankNumClass(idx)">{{ idx + 1 }}</span>
          <span class="rank-name">{{ row.spaceName || "—" }}</span>
          <span class="rank-val">{{ formatEnergyKwh(row.totalEnergy) }}</span>
        </li>
      </ul>
      <div
        v-else
        style="text-align: center; padding: 10px; color: rgba(255,255,255,0.25); font-size: 12px"
      >
        暂无排名数据
      </div>
      <div class="pv-rank-foot">
        光伏TOP：<span style="color: #ffa500">{{ topPvFoot }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import PvGaugeChart from "./PvGaugeChart.vue";
import {
  getGreenBenefit,
  getEnergyRatio,
  getSiteEnergyRank,
} from "@/network/photovoltaic";
import { getOverallEnvStats } from "@/network/measuring-point-history";

function fmtNum(n, digits = 1) {
  if (n == null || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString("zh-CN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function fmtPctRaw(v) {
  if (v == null || v === "") return "—";
  const x = Number(v);
  if (!Number.isFinite(x)) return "—";
  return (Math.round(x * 10) / 10).toFixed(1) + "%";
}

export default {
  name: "PvRightColumn",
  components: { PvGaugeChart },
  data() {
    return {
      green: null,
      env: null,
      rankPayload: null,
      pollTimer: null,
      pvRatio: null,
      gridRatio: null,
    };
  },
  computed: {
    co2Text() {
      return fmtNum(this.green && this.green.totalCo2Reduction, 2);
    },
    treesText() {
      const t = this.green && this.green.totalTreeEquivalent;
      if (t == null) return "—";
      return fmtNum(Number(t), 0);
    },
    monthGenMWhText() {
      const kwh = this.green && this.green.currentMonthGeneration;
      if (kwh == null || kwh === "") return "—";
      const mwh = Number(kwh) / 1000;
      if (!Number.isFinite(mwh)) return "—";
      return fmtNum(mwh, 2);
    },
    costSavedText() {
      const y = this.green && this.green.totalCostSaved;
      if (y == null || y === "") return "≈ ¥—";
      return `≈ ¥${fmtNum(y, 0)}`;
    },
    installedMwText() {
      const mw = this.green && this.green.totalInstalledPower;
      if (mw == null || mw === "") return "— MW";
      return `${fmtNum(mw, 2)} MW`;
    },
    pvRatioText() {
      return fmtPctRaw(this.pvRatio);
    },
    gridRatioText() {
      return fmtPctRaw(this.gridRatio);
    },
    envTemp() {
      const t = this.env && this.env.avgTemperature;
      if (!Number.isFinite(Number(t))) return "—";
      return `${fmtNum(Number(t), 1)}°C`;
    },
    envHumid() {
      const h = this.env && this.env.avgHumidity;
      if (!Number.isFinite(Number(h))) return "—";
      return `${fmtNum(Number(h), 1)}%`;
    },
    envPm25() {
      const e = this.env || {};
      const pm =
        e.avgPm25 != null
          ? e.avgPm25
          : e.pm25 != null
            ? e.pm25
            : e.avgPM25 != null
              ? e.avgPM25
              : null;
      if (!Number.isFinite(Number(pm))) return "—";
      return `${fmtNum(Number(pm), 0)}μg`;
    },
    envLight() {
      const r = this.env && this.env.avgLightRadiation;
      if (!Number.isFinite(Number(r))) return "—";
      return `${fmtNum(Number(r), 0)} W/m²`;
    },
    rankRows() {
      const d = this.rankPayload;
      if (!d || !Array.isArray(d.siteList)) return [];
      return d.siteList;
    },
    topPvFoot() {
      const m = this.rankPayload && this.rankPayload.maxPhotovoltaic;
      if (!m || m.spaceName == null) return "—";
      const kwh = m.photovoltaicEnergy;
      return `${m.spaceName} ${this.formatEnergyKwh(kwh)}`;
    },
  },
  mounted() {
    this.loadAll();
    this.pollTimer = setInterval(() => this.loadAll(), 90000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    rankNumClass(idx) {
      if (idx === 0) return "gold";
      if (idx === 1) return "silver";
      if (idx === 2) return "bronze";
      return "normal";
    },
    formatEnergyKwh(n) {
      if (n == null || n === "") return "—";
      const x = Number(n);
      if (!Number.isFinite(x)) return "—";
      return `${fmtNum(x, 0)}kWh`;
    },
    loadAll() {
      getGreenBenefit()
        .then((res) => {
          if (res && res.code === 0) this.green = res.data || null;
        })
        .catch(() => {});
      getEnergyRatio()
        .then((res) => {
          if (!res || res.code !== 0 || !res.data) return;
          this.pvRatio = res.data.photovoltaicRatio;
          this.gridRatio = res.data.gridRatio;
        })
        .catch(() => {});
      getOverallEnvStats()
        .then((res) => {
          if (res && res.code === 0) this.env = res.data || null;
        })
        .catch(() => {});
      getSiteEnergyRank()
        .then((res) => {
          if (res && res.code === 0) this.rankPayload = res.data || null;
        })
        .catch(() => {});
    },
    greenExport() {
      const g = this.green;
      const header =
        "累计减排CO2(吨),等效植树(棵),本月发电量(kWh),已节约电费(元),光伏总装机(MW)";
      const row = [
        g && g.totalCo2Reduction != null ? g.totalCo2Reduction : "",
        g && g.totalTreeEquivalent != null ? g.totalTreeEquivalent : "",
        g && g.currentMonthGeneration != null ? g.currentMonthGeneration : "",
        g && g.totalCostSaved != null ? g.totalCostSaved : "",
        g && g.totalInstalledPower != null ? g.totalInstalledPower : "",
      ].join(",");
      this.downloadCsv(`绿色效益_${moment().format("YYYYMMDD_HHmm")}.csv`, header, row);
    },
    rankExport() {
      const header = "排名,站点,总能耗(kWh),市电(kWh),光伏(kWh)";
      const lines = (this.rankRows || []).map((r, i) =>
        [
          i + 1,
          `"${String(r.spaceName || "").replace(/"/g, '""')}"`,
          r.totalEnergy != null ? r.totalEnergy : "",
          r.gridEnergy != null ? r.gridEnergy : "",
          r.photovoltaicEnergy != null ? r.photovoltaicEnergy : "",
        ].join(",")
      );
      const m = this.rankPayload && this.rankPayload.maxPhotovoltaic;
      const extra =
        m && m.spaceName
          ? `\n光伏最大站点,"${String(m.spaceName).replace(/"/g, '""')}",,,${m.photovoltaicEnergy != null ? m.photovoltaicEnergy : ""}`
          : "";
      this.downloadCsv(
        `站点能耗排名_${moment().format("YYYYMMDD_HHmm")}.csv`,
        header,
        lines.join("\n") + extra
      );
    },
    downloadCsv(filename, header, body) {
      const blob = new Blob([`\uFEFF${header}\n${body}`], {
        type: "text/csv;charset=utf-8",
      });
      const elink = document.createElement("a");
      elink.style.display = "none";
      elink.href = URL.createObjectURL(blob);
      elink.download = filename;
      document.body.appendChild(elink);
      elink.click();
      document.body.removeChild(elink);
    },
    resizeGauge() {
      const g = this.$refs.gaugeChart;
      if (g && typeof g.resizeChart === "function") g.resizeChart();
    },
  },
};
</script>
